/**
 * F-135 "any file on the board" — PURE helpers (no React, no DOM, no Yjs) for
 * the drop/paste layer and the file element's renderer: which upload route a
 * file takes, whether a drag is worth a drop ghost, how a file element is
 * first sized, and how its size reads.
 */
import type { FileMedia } from "@kola/shared";

import { isScriptFile } from "../import-export/script-file.js";

/** F-30: a dropped image's initial floating size is capped to this (board px). */
export const DROPPED_IMAGE_MAX_BOARD_PX = 480;

/** Initial floating size for a natural image (or video) size, longest side capped. */
export function cappedElementSize(w: number, h: number): { w: number; h: number } {
  const safeW = Math.max(1, w);
  const safeH = Math.max(1, h);
  const scale = Math.min(1, DROPPED_IMAGE_MAX_BOARD_PX / Math.max(safeW, safeH));
  return { w: Math.max(1, Math.round(safeW * scale)), h: Math.max(1, Math.round(safeH * scale)) };
}

/**
 * Which upload a dropped or pasted file takes:
 *  - "image": one of the four board-image formats — the image route (with the
 *    client-side re-encode, F-30) and an image element;
 *  - "script": a .txt/.csv/.tsv — the F-90 import, which owns those
 *    extensions outright (ImportScriptLayer takes the drop);
 *  - "file": everything else — the files route, and whatever element the
 *    server's sniff says (an audio clip, a video, a PDF, a mislabelled PNG).
 * The browser's `File.type` is an OS extension lookup and blank for anything
 * it does not know, which is why the image test is on the type and the
 * script test on the name.
 */
export type UploadRoute = "image" | "script" | "file";

const IMAGE_ROUTE_TYPES = new Set(["image/png", "image/jpeg", "image/gif", "image/webp"]);

export function uploadRouteFor(file: { type: string; name: string }): UploadRoute {
  if (IMAGE_ROUTE_TYPES.has(file.type)) return "image";
  if (isScriptFile(file.name)) return "script";
  return "file";
}

/**
 * Is a drag worth the drop ghost? Mid-drag only MIME types are visible, never
 * names, so this is a best guess: any file that is not text — a text type is
 * most likely a script for the F-90 import, and a blank type could be a .tsv
 * as easily as a .mkv, so neither earns the ghost. The DROP decides for real
 * with `uploadRouteFor`; a guess that was wrong costs the ghost, never the
 * behaviour.
 */
export function dragMayCarryUpload(items: Iterable<{ kind: string; type: string }>): boolean {
  for (const item of items) {
    if (item.kind !== "file") continue;
    if (item.type !== "" && !item.type.startsWith("text/")) return true;
  }
  return false;
}

/** Height (board px) of a file card's name strip — the part that is always a drag handle. */
export const FILE_CARD_HEADER_PX = 28;
/** A generic file card. */
export const FILE_CARD_W = 220;
export const FILE_CARD_H = 96;
/** An audio player card: the strip plus one row of controls. */
export const AUDIO_CARD_W = 320;
export const AUDIO_CARD_H = FILE_CARD_HEADER_PX + 48;
/** A video whose picture size is unknown takes a 16:9 box. */
const DEFAULT_VIDEO_W = 480;
const DEFAULT_VIDEO_H = 270;

/**
 * Initial size of a file element (board px): a video gets its picture at the
 * same cap as a dropped image plus the name strip; audio and everything else
 * take fixed cards. Also used for a CONTAINED (in-cell) file — every video box
 * is already under the F-26 column cap.
 */
export function fileElementSize(media: FileMedia, w?: number, h?: number): { w: number; h: number } {
  if (media === "video") {
    const picture = cappedElementSize(w ?? DEFAULT_VIDEO_W, h ?? DEFAULT_VIDEO_H);
    return { w: picture.w, h: picture.h + FILE_CARD_HEADER_PX };
  }
  if (media === "audio") return { w: AUDIO_CARD_W, h: AUDIO_CARD_H };
  return { w: FILE_CARD_W, h: FILE_CARD_H };
}

/** "512 B", "820 KB", "12.3 MB", "25 MB" — one decimal from a megabyte up, trailing .0 dropped. */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "";
  if (bytes < 1024) return `${Math.round(bytes)} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  const mb = bytes / (1024 * 1024);
  if (mb < 1024) return `${oneDecimal(mb)} MB`;
  return `${oneDecimal(mb / 1024)} GB`;
}

function oneDecimal(n: number): string {
  return n.toFixed(1).replace(/\.0$/, "");
}

/** The extension of a file name, lower-case, without the dot; "" when none. */
export function fileExtension(name: string): string {
  const dot = name.lastIndexOf(".");
  // `dot > 0` — a leading dot is a hidden file, not an extension.
  if (dot <= 0 || dot === name.length - 1) return "";
  return name.slice(dot + 1).toLowerCase();
}
